"use client";

import * as DialogPrimitive from "@radix-ui/react-dialog";
import { XIcon } from "lucide-react";
import * as React from "react";

import { useNextDrawerBackInterceptor } from "@/hooks/useNextDrawerBackInterceptor";
import { cn } from "@/lib/utils";
let KEY_ID = 0;
function Dialog(
    { enableBackInterceptor = true, ...props }: React.ComponentProps<typeof DialogPrimitive.Root> & {
        enableBackInterceptor?: boolean;
    },
) {
    // 使用 useRef 保证每个 Dialog 实例只分配一次 key，避免每次渲染递增
    const dialogKey = React.useRef( `__dialog__${KEY_ID++}` );
    const [ uncontrolledOpen, setUncontrolledOpen ] = React.useState( props.defaultOpen ?? false );
    const open = props.open ?? uncontrolledOpen;
    const onOpenChange = ( open: boolean ) => {
        setUncontrolledOpen( open );
        props.onOpenChange?.( open );
    };

    useNextDrawerBackInterceptor( {
        open,
        onClose: () => {
            onOpenChange( false );
        },
        enabled: enableBackInterceptor,
        key: dialogKey.current,
    } );

    return <DialogPrimitive.Root data-slot="dialog" {...props} open={open} onOpenChange={onOpenChange} />;
}

function DialogTrigger( { ...props }: React.ComponentProps<typeof DialogPrimitive.Trigger> ) {
    return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal( { ...props }: React.ComponentProps<typeof DialogPrimitive.Portal> ) {
    return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

function DialogClose( { ...props }: React.ComponentProps<typeof DialogPrimitive.Close> ) {
    return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

function DialogOverlay( { className, ...props }: React.ComponentProps<typeof DialogPrimitive.Overlay> ) {
    return (
        <DialogPrimitive.Overlay
            data-slot="dialog-overlay"
            className={cn(
                "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-[rgba(0,0,0,0.6)] dark:bg-[rgba(0,0,0,0.4)] backdrop-blur-[4px] max-w-[500px] mx-auto left-0 right-0",
                className,
            )}
            {...props}
        />
    );
}

function DialogContent(
    { className, children, showCloseButton = true, ...props }: React.ComponentProps<typeof DialogPrimitive.Content> & {
        showCloseButton?: boolean;
    },
) {
    return (
        <DialogPortal data-slot="dialog-portal">
            <DialogOverlay />
            <DialogPrimitive.Content
                data-slot="dialog-content"
                className={cn(
                    "border border-stroke bg-surface-subtle data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-90 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] fixed top-[50%] left-[50%] z-50 grid w-full max-w-[calc(100%-20px)] translate-x-[-50%] translate-y-[-50%] gap-4 rounded-lg p-6 shadow-lg duration-200 sm:max-w-xs",
                    className,
                )}
                {...props}
            >
                {children}
                {showCloseButton && (
                    <DialogPrimitive.Close
                        data-slot="dialog-close"
                        className="ring-offset-background focus:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground absolute top-4 right-4 rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"
                    >
                        <XIcon />
                        <span className="sr-only">Close</span>
                    </DialogPrimitive.Close>
                )}
            </DialogPrimitive.Content>
        </DialogPortal>
    );
}

function DialogHeader( { className, ...props }: React.ComponentProps<"div"> ) {
    return (
        <div
            data-slot="dialog-header"
            className={cn( "flex flex-col gap-2 text-center sm:text-left", className )}
            {...props}
        />
    );
}

function DialogFooter( { className, ...props }: React.ComponentProps<"div"> ) {
    return (
        <div
            data-slot="dialog-footer"
            className={cn( "flex flex-col-reverse gap-2 sm:flex-row sm:justify-end", className )}
            {...props}
        />
    );
}

function DialogTitle( { className, ...props }: React.ComponentProps<typeof DialogPrimitive.Title> ) {
    return (
        <DialogPrimitive.Title
            data-slot="dialog-title"
            className={cn( "text-lg leading-none font-semibold", className )}
            {...props}
        />
    );
}

function DialogDescription( { className, ...props }: React.ComponentProps<typeof DialogPrimitive.Description> ) {
    return (
        <DialogPrimitive.Description
            data-slot="dialog-description"
            className={cn( "text-muted-foreground text-sm", className )}
            {...props}
        />
    );
}

export {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogOverlay,
    DialogPortal,
    DialogTitle,
    DialogTrigger,
};
